import "@/App.css";
import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ModuleSelection from "@/pages/ModuleSelection";
import { clearCache } from "@/utils/apiCache";

// Lazy load all pages for better performance
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PurchaseOrders = lazy(() => import("@/pages/PurchaseOrders"));
const PackingLists = lazy(() => import("@/pages/PackingLists"));
const ScanningStation = lazy(() => import("@/pages/ScanningStation"));
const CartonLabels = lazy(() => import("@/pages/CartonLabels"));
const EmailSettings = lazy(() => import("@/pages/EmailSettings"));
const PackingProgress = lazy(() => import("@/pages/PackingProgress"));
const CompletePackingView = lazy(() => import("@/pages/CompletePackingView"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const BuyerPackingList = lazy(() => import("@/pages/BuyerPackingList"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const DispatchTracking = lazy(() => import("@/pages/DispatchTracking"));
const SampleInventory = lazy(() => import("@/pages/SampleInventory"));
const DesignLibrary = lazy(() => import("@/pages/DesignLibrary"));

// Purchase Module - Lazy loaded
const Vendors = lazy(() => import("@/pages/purchase/Vendors"));
const ProposedVendors = lazy(() => import("@/pages/purchase/ProposedVendors"));
const BuyerOrders = lazy(() => import("@/pages/purchase/BuyerOrders"));
const POConsolidation = lazy(() => import("@/pages/purchase/POConsolidation"));
const BuyerOrdersTable = lazy(() => import("@/pages/purchase/BuyerOrdersTable"));
const BillOfMaterials = lazy(() => import("@/pages/purchase/BillOfMaterials"));
const RawMaterialPO = lazy(() => import("@/pages/purchase/RawMaterialPO"));
const LabDipRateCard = lazy(() => import("@/pages/purchase/LabDipRateCard"));
const BuyerApprovalCards = lazy(() => import("@/pages/purchase/BuyerApprovalCards"));
const WorkerScanner = lazy(() => import("@/pages/WorkerScanner"));
const CourierTracking = lazy(() => import("@/pages/purchase/CourierTracking"));
const OutwardChallans = lazy(() => import("@/pages/purchase/OutwardChallans"));
const VendorFollowUp = lazy(() => import("@/pages/purchase/VendorFollowUp"));
const GRNReport = lazy(() => import("@/pages/purchase/GRNReport"));
const ReturnChallans = lazy(() => import("@/pages/purchase/ReturnChallans"));
const PurchaseReports = lazy(() => import("@/pages/purchase/PurchaseReports"));
const Costing = lazy(() => import("@/pages/purchase/Costing"));
const CostingSheet = lazy(() => import("@/pages/purchase/CostingSheet"));
const BOMTracking = lazy(() => import("@/pages/purchase/BOMTracking"));
const VendorPerformance = lazy(() => import("@/pages/purchase/VendorPerformance"));
const PriceHistory = lazy(() => import("@/pages/purchase/PriceHistory"));
const StyleMaster = lazy(() => import("@/pages/purchase/StyleMaster"));
const MiscellaneousPO = lazy(() => import("@/pages/purchase/MiscellaneousPO"));
const ProductionPlan = lazy(() => import("@/pages/purchase/ProductionPlan"));
const SampleStatusReport = lazy(() => import("@/pages/purchase/SampleStatusReport"));
const StyleTNAReport = lazy(() => import("@/pages/purchase/StyleTNAReport"));
const TNAReport = lazy(() => import("@/pages/purchase/TNAReport"));
const OrdersReport = lazy(() => import("@/pages/purchase/OrdersReport"));
const BuyerOrderValue = lazy(() => import("@/pages/purchase/BuyerOrderValue"));

// PackFlow Module - Additional pages
const PackingListBabyhug = lazy(() => import("@/pages/packflow/PackingListBabyhug"));

// Marketing Module - Lazy loaded
const MarketingDashboard = lazy(() => import("@/pages/marketing/MarketingDashboard"));
const CurrentBuyers = lazy(() => import("@/pages/marketing/CurrentBuyers"));
const ProspectiveBuyers = lazy(() => import("@/pages/marketing/ProspectiveBuyers"));
const CompanyProfile = lazy(() => import("@/pages/marketing/CompanyProfile"));

// Accounts Module - Lazy loaded
const AccountsDashboard = lazy(() => import("@/pages/accounts/AccountsDashboard"));
const AccountsChecklist = lazy(() => import("@/pages/accounts/AccountsChecklist"));
const Salary = lazy(() => import("@/pages/accounts/Salary"));
const Bills = lazy(() => import("@/pages/accounts/Bills"));
const DailyExpenses = lazy(() => import("@/pages/accounts/DailyExpenses"));
const WagesExpenses = lazy(() => import("@/pages/accounts/WagesExpenses"));

// Fabric Store Module - Lazy loaded
const FabricStore = lazy(() => import("@/pages/store/FabricStore"));
const StoreIssueReceive = lazy(() => import("@/pages/store/StoreIssueReceive"));
const StoreStyleTracker = lazy(() => import("@/pages/store/StoreStyleTracker"));
const StoreReports = lazy(() => import("@/pages/store/StoreReports"));

// Delegation Module - Lazy loaded
const Delegation = lazy(() => import("@/pages/Delegation"));

// Cutting Module - Lazy loaded
const CuttingIssueReceive = lazy(() => import("@/pages/cutting/IssueReceiveSimple"));
const CuttingOrders = lazy(() => import("@/pages/cutting/CuttingOrders"));
const CuttingPayments = lazy(() => import("@/pages/cutting/CuttingPayments"));
const ProductionFlow = lazy(() => import("@/pages/cutting/ProductionFlow"));

// Unit Two Module - Lazy loaded (now Productivity)
const Productivity = lazy(() => import("@/pages/productivity/Productivity"));
const UnitTwo = lazy(() => import("@/pages/unit-two/UnitTwo")); // Keep for backward compatibility

// Quality Review Module - Lazy loaded
const QualityReview = lazy(() => import("@/pages/QualityReview"));

// Sampling Module - Lazy loaded
const Sampling = lazy(() => import("@/pages/Sampling"));
const SampleStitchPlan = lazy(() => import("@/pages/sampling/SampleStitchPlan"));
const SamplingCosting = lazy(() => import("@/pages/sampling/SamplingCosting"));

// Barcode Generation Module - Lazy loaded
const BarcodeGeneration = lazy(() => import("@/pages/BarcodeGeneration"));

// Kidsdew Module - Lazy loaded
const KidsdewDashboard = lazy(() => import("@/pages/kidsdew/KidsdewDashboard"));
const KidsdewProducts = lazy(() => import("@/pages/kidsdew/KidsdewProducts"));
const KidsdewContent = lazy(() => import("@/pages/kidsdew/KidsdewContent"));
const KidsdewOrders = lazy(() => import("@/pages/kidsdew/KidsdewOrders"));
const KidsdewPosts = lazy(() => import("@/pages/kidsdew/KidsdewPosts"));
const KidsdewReconciliation = lazy(() => import("@/pages/kidsdew/KidsdewReconciliation"));
const KidsdewUniware = lazy(() => import("@/pages/kidsdew/KidsdewUniware"));
const KidsdewBuyers = lazy(() => import("@/pages/kidsdew/KidsdewBuyers"));
const BuyerSOP = lazy(() => import("@/pages/BuyerSOP"));

// Drive Module - Lazy loaded
const Drive = lazy(() => import("@/pages/Drive"));

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Set up axios interceptor for auth token
// Note: We intentionally do NOT add Cache-Control / Pragma headers globally — those
// custom request headers force a CORS preflight on every API call and disable
// browser caching, both of which severely hurt perceived performance.
// Mutations clear the in-memory apiCache (`clearCache()`); individual endpoints
// can pass `params: { _: Date.now() }` if they truly need cache-bust per call.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Protected route wrapper
function ProtectedRoute({ children, isAuthenticated }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Router component that checks for session_id in hash
function AppRouter({ isAuthenticated, onLogin, onLogout }) {
  const location = useLocation();
  
  // Check for session_id in URL hash (from Google OAuth redirect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Check for password reset token
  if (location.pathname === "/reset-password" || location.search?.includes('token=')) {
    return <LoginPage onLogin={onLogin} />;
  }
  
  // Loading fallback for lazy loaded components
  const LoadingFallback = () => (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
        <p className="text-zinc-500">Loading...</p>
      </div>
    </div>
  );
  
  return (
    <Suspense fallback={<LoadingFallback />}>
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <LoginPage onLogin={onLogin} />
      } />
      <Route path="/reset-password" element={<LoginPage onLogin={onLogin} />} />
      
      {/* Module Selection - Landing Page */}
      <Route path="/" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <ModuleSelection />
        </ProtectedRoute>
      } />
      
      {/* PackFlow Module */}
      <Route path="/packflow" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="packflow" />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="packing-lists" element={<PackingLists />} />
        <Route path="sample-inventory" element={<SampleInventory />} />
        <Route path="scanning" element={<ScanningStation />} />
        <Route path="labels" element={<CartonLabels />} />
        <Route path="progress" element={<PackingProgress />} />
        <Route path="buyer-packing-list" element={<BuyerPackingList />} />
        <Route path="packing-list-babyhug" element={<PackingListBabyhug />} />
        <Route path="email-settings" element={<EmailSettings />} />
        <Route path="packing-view" element={<CompletePackingView />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="dispatch" element={<DispatchTracking />} />
      </Route>
      
      {/* Purchase Module */}
      <Route path="/purchase" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="purchase" />
        </ProtectedRoute>
      }>
        <Route index element={<BuyerOrders />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="proposed-vendors" element={<ProposedVendors />} />
        <Route path="vendor-performance" element={<VendorPerformance />} />
        <Route path="price-history" element={<PriceHistory />} />
        <Route path="buyer-orders" element={<BuyerOrders />} />
        <Route path="buyer-orders-table" element={<BuyerOrdersTable />} />
        <Route path="buyer-order-value" element={<BuyerOrderValue />} />
        <Route path="bom" element={<BillOfMaterials />} />
        <Route path="bom-tracking" element={<BOMTracking />} />
        <Route path="raw-material-po" element={<RawMaterialPO />} />
        <Route path="lab-dip-rate-card" element={<LabDipRateCard />} />
        <Route path="buyer-approval-cards" element={<BuyerApprovalCards />} />
        <Route path="courier-tracking" element={<CourierTracking />} />
        <Route path="outward-challans" element={<OutwardChallans />} />
        <Route path="vendor-follow-up" element={<VendorFollowUp />} />
        <Route path="grn" element={<GRNReport />} />
        <Route path="return-challans" element={<ReturnChallans />} />
        <Route path="costing" element={<Costing />} />
        <Route path="costing-sheet" element={<CostingSheet />} />
        <Route path="style-master" element={<StyleMaster />} />
        <Route path="reports" element={<PurchaseReports />} />
        <Route path="miscellaneous-po" element={<MiscellaneousPO />} />
        <Route path="production-plan" element={<ProductionPlan />} />
        <Route path="sample-status-report" element={<SampleStatusReport />} />
        <Route path="style-tna" element={<StyleTNAReport />} />
        <Route path="po-consolidation" element={<POConsolidation />} />
        <Route path="tna-report" element={<TNAReport />} />
        <Route path="orders-report" element={<OrdersReport />} />
      </Route>
      
      {/* Marketing Module */}
      <Route path="/marketing" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="marketing" />
        </ProtectedRoute>
      }>
        <Route index element={<MarketingDashboard />} />
        <Route path="current-buyers" element={<CurrentBuyers />} />
        <Route path="prospective-buyers" element={<ProspectiveBuyers />} />
        <Route path="company-profile" element={<CompanyProfile />} />
      </Route>
      
      {/* Accounts Module */}
      <Route path="/accounts" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="accounts" />
        </ProtectedRoute>
      }>
        <Route index element={<AccountsDashboard />} />
        <Route path="checklist" element={<AccountsChecklist />} />
        <Route path="salary" element={<Salary />} />
        <Route path="bills" element={<Bills />} />
        <Route path="daily-expenses" element={<DailyExpenses />} />
        <Route path="wages-expenses" element={<WagesExpenses />} />
      </Route>
      
      {/* Fabric Store Module */}
      <Route path="/store" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="store" />
        </ProtectedRoute>
      }>
        <Route index element={<FabricStore />} />
        <Route path="issue-receive" element={<StoreIssueReceive />} />
        <Route path="style-tracker" element={<StoreStyleTracker />} />
        <Route path="reports" element={<StoreReports />} />
      </Route>
      
      {/* Delegation Module */}
      <Route path="/delegation" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="delegation" />
        </ProtectedRoute>
      }>
        <Route index element={<Delegation />} />
      </Route>
      
      {/* Cutting Module */}
      <Route path="/cutting" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="cutting" />
        </ProtectedRoute>
      }>
        <Route index element={<CuttingIssueReceive />} />
        <Route path="issue-receive" element={<CuttingIssueReceive />} />
        <Route path="orders" element={<CuttingOrders />} />
        <Route path="payments" element={<CuttingPayments />} />
        <Route path="flow" element={<ProductionFlow />} />
      </Route>
      
      {/* Productivity Module (formerly Unit Two) */}
      <Route path="/productivity" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="productivity" />
        </ProtectedRoute>
      }>
        <Route index element={<Productivity />} />
      </Route>

      {/* Worker Scanner Module */}
      <Route path="/worker-scanner" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="worker-scanner" />
        </ProtectedRoute>
      }>
        <Route index element={<WorkerScanner />} />
      </Route>

      {/* Legacy Unit Two route - redirect to productivity */}
      <Route path="/unit-two" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="productivity" />
        </ProtectedRoute>
      }>
        <Route index element={<Productivity />} />
      </Route>
      
      {/* Quality Review Module */}
      <Route path="/quality" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="quality" />
        </ProtectedRoute>
      }>
        <Route index element={<QualityReview />} />
      </Route>
      
      {/* Sampling Module */}
      <Route path="/sampling" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="sampling" />
        </ProtectedRoute>
      }>
        <Route index element={<Sampling />} />
        <Route path="design-library" element={<DesignLibrary />} />
        <Route path="stitch-plan" element={<SampleStitchPlan />} />
        <Route path="costing" element={<SamplingCosting />} />
      </Route>
      
      {/* Barcode Generation Module */}
      <Route path="/barcode" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <BarcodeGeneration />
        </ProtectedRoute>
      } />

      {/* Kidsdew Module */}
      <Route path="/kidsdew" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="kidsdew" />
        </ProtectedRoute>
      }>
        <Route index element={<KidsdewDashboard />} />
        <Route path="products" element={<KidsdewProducts />} />
        <Route path="content" element={<KidsdewContent />} />
        <Route path="orders" element={<KidsdewOrders />} />
        <Route path="posts" element={<KidsdewPosts />} />
        <Route path="reconciliation" element={<KidsdewReconciliation />} />
        <Route path="uniware" element={<KidsdewUniware />} />
        <Route path="buyers" element={<KidsdewBuyers />} />
        <Route path="dispatch" element={<DispatchTracking />} />
      </Route>

      {/* Buyer SOP Module */}
      <Route path="/buyer-sop" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="buyer-sop" />
        </ProtectedRoute>
      }>
        <Route index element={<BuyerSOP />} />
      </Route>

      {/* Drive Module */}
      <Route path="/drive" element={
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Layout onLogout={onLogout} moduleType="drive" />
        </ProtectedRoute>
      }>
        <Route index element={<Drive />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Check if user is authenticated on mount
    const token = localStorage.getItem("auth_token");
    if (token) {
      // Verify token is still valid
      axios.get(`${API_URL}/api/auth/verify`)
        .then(() => {
          setIsAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("username");
          setIsAuthenticated(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);
  
  const handleLogin = (token, username) => {
    localStorage.setItem("auth_token", token);
    localStorage.setItem("username", username);
    // Clear any cached data to ensure fresh data on login
    clearCache();
    setIsAuthenticated(true);
  };
  
  const handleLogout = () => {
    // Clear all cached API responses
    clearCache();
    // Clear localStorage
    localStorage.removeItem("auth_token");
    localStorage.removeItem("username");
    // Clear any sessionStorage
    sessionStorage.clear();
    setIsAuthenticated(false);
    // Force reload to clear all React state and cached components
    window.location.href = "/login";
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter 
          isAuthenticated={isAuthenticated} 
          onLogin={handleLogin} 
          onLogout={handleLogout}
        />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
